import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import BitwardenConnectForm from "./BitwardenConnectForm";
import bitwarden_step_1 from "./images/bitwarden_step_1.png";
import bitwarden_step_2 from "./images/bitwarden_step_2.png";
import bitwarden_step_3 from "./images/bitwarden_step_3.png";

const BitwardenIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const [region, setRegion] = useState("");
  const queryClient = useQueryClient();

  const bitwardenRegionOptions = ["bitwarden.com", "bitwarden.eu"];
  const bitwardenLoginURL = region ? `https://vault.${region}/#/login` : "";

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("bitwarden");
  const [loading, setLoading] = useState(false);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerBitwardenIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    if (!region) {
      Toast.error("Please select your Bitwarden region");
      setLoading(false);
      return;
    }
    registerBitwardenIntegration(values.client_id, values.client_secret, region)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Bitwarden integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated && !loading && !isIntegratedLoading
            ? "Once integrated, the Breeze platform will automatically read the configurations from Bitwarden and prioritize the suggested remediations."
            : ""
        }
        image={logos.BitwardenLogo}
        name={"Bitwarden"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Bitwarden is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Login to Bitwarden">
                  <Typography variant={"body2"}>
                    Select the region where your Bitwarden account is hosted in
                  </Typography>
                  <FormControl fullWidth sx={{ width: 220 }}>
                    <InputLabel id="region-select-label">Region</InputLabel>
                    <Select
                      labelId="region-select-label"
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                      label="Region"
                    >
                      {bitwardenRegionOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant={"body2"}>
                    Login to your Bitwarden account.
                  </Typography>
                  <Box style={{ display: "flex" }}>
                    <PrimaryButton
                      onClick={() => {
                        window.open(bitwardenLoginURL, "_blank");
                      }}
                      disabled={!region}
                    >
                      Login to Bitwarden
                    </PrimaryButton>
                  </Box>
                </GuidelineStep>

                <GuidelineStep title="Get the client ID and client secret">
                  <Typography variant={"body2"}>
                    From the left menu go to{" "}
                    <b>Admin Console {">"} Settings </b>
                  </Typography>
                  <Typography variant={"body2"}>
                    Select <b>Organization info</b> and click on{" "}
                    <b>View API key</b>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: bitwarden_step_1,
                        altext: "Bitwarden Security Page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Enter your password and click <b>View API key</b>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: bitwarden_step_2,
                        altext: "Bitwarden View API Key",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    A pop up will appear. Copy the client ID and client secret
                    and paste them in the fields below.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: bitwarden_step_3,
                        altext: "Bitwarden client ID and client secret",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Paste the client ID and client secret here">
                  <Box width={700}>
                    <BitwardenConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default BitwardenIntegration;
